import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, FormControl } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useLocaleProvider } from "../../components/locale-provider";
import { SectionHeading } from "../../components/section-heading";
import Selector from "../../components/selector";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { ACCESS_DENIED } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { sendReservationMessage } from "./infrastructure/guestAssistant.api";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.utilities.guestAssistant",
      defaultMessage: "Guest Assistant - Reservation Simulator",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    reservationId: {
      id: "guestAssistant.input.reservationId",
      defaultMessage: "Reservation Id",
    },
    phoneNumber: {
      id: "guestAssistant.input.phoneNumber",
      defaultMessage: "Phone Number",
    },
    locale: {
      id: "guestAssistant.input.locale",
      defaultMessage: "Language",
    },
    guestName: {
      id: "guestAssistant.input.guestName",
      defaultMessage: "Guest Name",
    },
    guestLastname: {
      id: "guestAssistant.input.guestLastname",
      defaultMessage: "Guest Lastname",
    },
    sendMessage: {
      id: "guestAssistant.button.sendMessage",
      defaultMessage: "Send message",
    },
    success: {
      id: "app.success",
      defaultMessage: "Success",
    },
    phoneValidationError: {
      id: "guestAssistant.error.phoneValidation",
      defaultMessage: "Phone must be in format: 34655874666",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  gap: 16px;
`;

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

function GuestAssistantManager() {
  const userPropertyTickersList = useUserPropertyTickers();
  const config = useConfig();
  const httpClient = useHttpClient(config.guestAssistantApiEndpoint);
  const { shortLocale } = useLocaleProvider();

  const [bookingId, setBookingId] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [locale, setLocale] = useState<string>(shortLocale);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );

  const handleReservationIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setBookingId(event.target.value),
    []
  );

  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setPhone(event.target.value),
    []
  );

  const handleLocaleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setLocale(event.target.value),
    []
  );

  useEffect(() => {
    setPropertyTickerOptions(
      userPropertyTickersList.map(option => ({ value: option, label: option }))
    );
  }, [userPropertyTickersList]);

  const handleSelectPropertyTicker = useCallback(
    (selectedOption: Option) => setPropertyTickerOption(selectedOption),
    []
  );

  const validatePhone = (phoneNumber: string): boolean => {
    // Validate format like "34655874666" (digits only, similar length)
    const phoneRegex = /^\d{10,15}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleSendMessage = useCallback(async () => {
    if (!propertyTickerOption?.value || !bookingId || !phone) {
      setError({ message: "All fields are required" });
      return;
    }

    if (!validatePhone(phone)) {
      setError({ message: "Phone must be in format: 34655874666" });
      return;
    }

    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);

    try {
      await sendReservationMessage(httpClient, {
        propertyTicker: propertyTickerOption.value,
        reservationId: bookingId,
        locale,
        phone,
      });
      console.log("sendReservationMessage DONE");
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error sending reservation message", e);
        setError({ message: "Error sending reservation message" });
      }
    }
    setLoading(false);
  }, [bookingId, httpClient, locale, phone, propertyTickerOption?.value]);

  if (loading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
          <Label>
            <FormattedMessage {...messages.reservationId} />
          </Label>
          <FormControl
            type="text"
            placeholder="MXXXXXX"
            value={bookingId}
            onChange={handleReservationIdChange}
          />
          <Label>
            <FormattedMessage {...messages.phoneNumber} />
          </Label>
          <FormControl
            type="text"
            placeholder="34655874666"
            value={phone}
            onChange={handlePhoneChange}
          />
          <Label>
            <FormattedMessage {...messages.locale} />
          </Label>
          <FormControl
            type="text"
            placeholder="es|en|fr"
            value={locale}
            onChange={handleLocaleChange}
          />
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>{error.message}</strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.success} />
            </strong>
          </Alert>
        )}

        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleSendMessage}>
            <FormattedMessage {...messages.sendMessage} />
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(GuestAssistantManager);
